import { useCallback, useEffect, useState } from 'react';
import type {
  PaigeExportTiming,
  PaigeExportTimingInput,
  PaigeExportsChannel,
} from './export-types';

/** The visible-tab look again for what is held: a daily time passing or another person's Export now (no costly polling: 30 s). */
const LOOK_AGAIN_MS = 30_000;

const failed = (failure: unknown, otherwise: string) =>
  failure instanceof Error ? failure.message : otherwise;

/** "12 documents are on their way." */
export const sentLine = (sent: number) =>
  sent === 1 ? '1 document is on its way.' : `${sent.toLocaleString()} documents are on their way.`;

/**
 * The job's export timing from Paige's bridge: read on mount and every 30 s while the tab is visible, saved with a
 * roll back on refusal, and Export now. `note` is the last outcome in a sentence ("12 documents are on their way").
 */
export function useExportTiming(channel: PaigeExportsChannel | undefined) {
  const [timing, setTiming] = useState<PaigeExportTiming | null>(null);
  const [busy, setBusy] = useState<'saving' | 'sending' | null>(null);
  const [note, setNote] = useState<string | null>(null);
  useLookAgain(channel, setTiming);
  /** One action at a time: busy while it runs, its outcome in the note. */
  const run = async (
    kind: 'saving' | 'sending',
    work: (c: PaigeExportsChannel) => Promise<string | null>,
  ) => {
    if (!channel || busy) return;
    setBusy(kind);
    setNote(null);
    try {
      setNote(await work(channel));
    } finally {
      setBusy(null);
    }
  };
  const save = (input: PaigeExportTimingInput) =>
    run('saving', (c) => saved(c, input, timing, setTiming));
  const exportNow = () =>
    run('sending', async (c) => {
      try {
        const sent = await c.release();
        setTiming(await c.timing());
        return sentLine(sent);
      } catch (failure) {
        return failed(failure, 'Paige could not send them');
      }
    });
  return { timing, busy, note, save, exportNow };
}

/** Shows the choice at once, saves it, and puts back what was there when Paige refuses it; the refusal, or null. */
async function saved(
  channel: PaigeExportsChannel,
  input: PaigeExportTimingInput,
  before: PaigeExportTiming | null,
  set: (timing: PaigeExportTiming | null) => void,
): Promise<string | null> {
  if (before)
    set({ ...before, when: input.when, at: input.at ?? null, paused: input.paused ?? false });
  try {
    set(await channel.setTiming(input));
    return null;
  } catch (failure) {
    set(before);
    return failed(failure, 'Paige could not save the change');
  }
}

/** Reads the timing now and every 30 s while the tab is visible. */
function useLookAgain(
  channel: PaigeExportsChannel | undefined,
  set: (timing: PaigeExportTiming) => void,
) {
  const read = useCallback(() => {
    if (!channel || document.visibilityState === 'hidden') return;
    channel
      .timing()
      .then(set)
      .catch(() => undefined);
  }, [channel, set]);
  useEffect(() => {
    read();
    const timer = window.setInterval(read, LOOK_AGAIN_MS);
    return () => window.clearInterval(timer);
  }, [read]);
}
